import { useCallback, useEffect, useRef, useState } from 'react';
import { bidDomainApi } from './api/bidDomain';

interface UseSpeechToTextOptions {
  baseText?: string;
  onFinalTranscript?: (text: string) => void;
}

/** WAV خام — APIهای Whisper معمولاً WebM بدون duration را رد می‌کنند */
function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const numChannels = 1;
  const bitsPerSample = 16;
  const blockAlign = (numChannels * bitsPerSample) / 8;
  const byteRate = sampleRate * blockAlign;
  const dataSize = samples.length * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

function downsampleTo16k(input: Float32Array, inputRate: number): Float32Array {
  if (inputRate === 16000) return input;
  const ratio = inputRate / 16000;
  const outLen = Math.max(1, Math.floor(input.length / ratio));
  const output = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    let count = 0;
    for (let j = start; j < end; j++) {
      sum += input[j];
      count++;
    }
    output[i] = count > 0 ? sum / count : input[start] ?? 0;
  }
  return output;
}

export function useSpeechToText({
  baseText = '',
  onFinalTranscript,
}: UseSpeechToTextOptions = {}) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const chunksRef = useRef<Float32Array[]>([]);
  const sampleRateRef = useRef(48000);
  const baseTextRef = useRef(baseText);
  const onFinalRef = useRef(onFinalTranscript);

  useEffect(() => {
    baseTextRef.current = baseText;
  }, [baseText]);

  useEffect(() => {
    onFinalRef.current = onFinalTranscript;
  }, [onFinalTranscript]);

  useEffect(() => {
    const ok =
      typeof navigator !== 'undefined' &&
      Boolean(navigator.mediaDevices?.getUserMedia) &&
      typeof AudioContext !== 'undefined';
    setSupported(ok);
  }, []);

  const cleanup = useCallback(() => {
    try {
      processorRef.current?.disconnect();
      sourceRef.current?.disconnect();
    } catch {
      /* ignore */
    }
    processorRef.current = null;
    sourceRef.current = null;
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const transcribeWav = useCallback(async (blob: Blob) => {
    setTranscribing(true);
    setError(null);
    try {
      const res = await bidDomainApi.speechToText(blob, 'audio/wav');
      if (!res.success || !res.text?.trim()) {
        throw new Error(res.message ?? 'متنی تشخیص داده نشد.');
      }
      const combined = [baseTextRef.current.trim(), res.text.trim()].filter(Boolean).join(' ');
      onFinalRef.current?.(combined);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در تبدیل گفتار به متن');
    } finally {
      setTranscribing(false);
    }
  }, []);

  const stop = useCallback(() => {
    if (!listening && !streamRef.current) return;

    const chunks = chunksRef.current;
    chunksRef.current = [];
    const rate = sampleRateRef.current;
    cleanup();
    setListening(false);

    if (chunks.length === 0) {
      setError('صدایی ضبط نشد. کمی بلندتر صحبت کنید و دوباره تلاش کنید.');
      return;
    }

    let total = 0;
    for (const c of chunks) total += c.length;
    const merged = new Float32Array(total);
    let offset = 0;
    for (const c of chunks) {
      merged.set(c, offset);
      offset += c.length;
    }

    // حداقل ~۰٫۴ ثانیه صدا
    if (merged.length < rate * 0.35) {
      setError('ضبط خیلی کوتاه بود. کمی بیشتر صحبت کنید.');
      return;
    }

    const pcm16k = downsampleTo16k(merged, rate);
    const wav = encodeWav(pcm16k, 16000);
    void transcribeWav(wav);
  }, [listening, cleanup, transcribeWav]);

  const start = useCallback(async () => {
    if (!supported) {
      setError('مرورگر از ضبط صدا پشتیبانی نمی‌کند.');
      return;
    }
    if (transcribing || listening) return;

    setError(null);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });
      streamRef.current = stream;

      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();

      sampleRateRef.current = ctx.sampleRate;
      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;

      // bufferSize 4096 — سازگار و سبک
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (ev) => {
        const input = ev.inputBuffer.getChannelData(0);
        chunksRef.current.push(new Float32Array(input));
      };

      // اتصال به destination لازم است تا بعضی مرورگرها پردازش را قطع نکنند (با gain صفر)
      const mute = ctx.createGain();
      mute.gain.value = 0;
      source.connect(processor);
      processor.connect(mute);
      mute.connect(ctx.destination);

      setListening(true);
    } catch (e) {
      cleanup();
      setListening(false);
      const msg = e instanceof Error ? e.message : '';
      setError(
        msg.includes('Permission') || msg.includes('NotAllowed') || msg.includes('Denied')
          ? 'دسترسی به میکروفون داده نشد. از قفل کنار نوار آدرس اجازه بدهید.'
          : 'خطا در دسترسی به میکروفون.'
      );
    }
  }, [supported, transcribing, listening, cleanup]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else void start();
  }, [listening, start, stop]);

  useEffect(
    () => () => {
      cleanup();
    },
    [cleanup]
  );

  return {
    supported,
    listening,
    transcribing,
    interim: '',
    error,
    start,
    stop,
    toggle,
  };
}
